"use client";

import dynamic from "next/dynamic";
import React, {FC} from "react";
import {editor} from "monaco-editor";
import { useTheme } from "@/app/ThemeProvider";
import { useEditorPrefs } from "@/app/EditorPrefsProvider";

const EDITOR_HEIGHT = "min(70vh, 600px)";

const Editor = dynamic(() => import("@monaco-editor/react"), {
    ssr: false,
    loading: () => (
        <div
            className="w-full flex items-center justify-center bg-gray-50 dark:bg-slate-800 animate-pulse"
            style={{ height: EDITOR_HEIGHT }}
            role="status"
            aria-label="Loading editor"
        >
            <span className="text-sm text-gray-400 dark:text-gray-500">Loading editor…</span>
        </div>
    ),
});

interface EditorWrapperProps{
    language: string;
    value: string;
    readOnly?: boolean;
    isValid?: boolean;
    showValidationPanel?: boolean;
    changeHandler?: (value: string | undefined) => void;
    setEditor?: React.Dispatch<React.SetStateAction<editor.IStandaloneCodeEditor | null>>;
    setMonacoEditor?: React.Dispatch<React.SetStateAction<typeof editor | null>>;
    placeholder?: string;
}

const EditorWrapper: FC<EditorWrapperProps> = (props: EditorWrapperProps) => {
    const {
        language,
        value,
        changeHandler,
        setEditor,
        setMonacoEditor,
        isValid,
        readOnly,
        showValidationPanel,
        placeholder} = props;
    const { theme } = useTheme();
    const { wordWrap, minimap, fontSize } = useEditorPrefs();

    const editorClassName = showValidationPanel? " " : " border-1 pt-10"
    const editorContainerBorder = showValidationPanel ? " border-2" : " border-2";
    let editorContainerColor = isValid ? " border-green-500" : " border-red-500";
    editorContainerColor = !showValidationPanel ? " border-gray-200 dark:border-gray-700" : editorContainerColor;
    const showPlaceholder = readOnly && !value && !!placeholder;

    return (
        <div className={" w-full " + editorContainerBorder + editorContainerColor}>
            <div className="flex items-center justify-end">
                {(!showValidationPanel) && <div className="h-6"></div>}
                { (showValidationPanel && !isValid) && <p className="bg-red-500 text-small w-fit relative py-0 px-5 text-white rounded-bl h-6">Format Invalid</p>}
                { (showValidationPanel && isValid) && <p className="bg-green-500 text-small w-fit relative py-0 px-5 text-white rounded-bl h-6">Format Valid</p>}
            </div>

            {showPlaceholder ? (
                <div
                    className="w-full flex items-center justify-center text-center px-8 bg-gray-50 dark:bg-slate-800"
                    style={{ height: EDITOR_HEIGHT }}
                >
                    <p className="text-sm text-gray-400 dark:text-gray-500">{placeholder}</p>
                </div>
            ) : (
                <Editor
                        height={EDITOR_HEIGHT}
                        theme={theme === "dark" ? "vs-dark" : "light"}
                        defaultLanguage={language}
                        language={language}
                        value={value}
                        onChange={changeHandler}

                        options={{
                            readOnly:readOnly,
                            fontSize,
                            wordWrap: wordWrap ? "on" : "off",
                            quickSuggestions: {
                                other: false,
                                comments: false,
                                strings: false
                            },
                            parameterHints: {
                                enabled: false
                            },
                            suggestOnTriggerCharacters: false,
                            acceptSuggestionOnEnter: "off",
                            tabCompletion: "off",
                            wordBasedSuggestions: "off",
                            suggest: {
                                showClasses: false,
                                showEnums: false,
                                showFields: false,
                                showFiles: false,
                                showStructs:false,
                                showWords: false
                            },
                            overviewRulerLanes: 0,
                            contextmenu: false,
                            minimap: {
                                enabled: minimap
                            },
                            scrollbar: {
                                useShadows: false, vertical: "auto",
                                verticalHasArrows: false,
                                verticalSliderSize: 5
                            },
                        }}
                        onMount={(editor, monaco) => {
                            //disabling auto validation for json
                            monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
                                validate: false
                            })

                            if (!!setEditor) {
                                setEditor(editor)
                            }
                            if (!!setMonacoEditor) {
                                setMonacoEditor(monaco.editor)
                            }
                        }}
                />
            )}
        </div>

    )
}

export default EditorWrapper;
